import React, { useRef } from 'react';
import { useDrop, useDrag, DragSourceMonitor } from 'react-dnd';
import { ItemType } from './constants';

interface DragItem {
  index: number;
  id: string;
  type: ItemType;
}

interface Props {
  text?: string;
  children?: React.ReactNode;
  index: number;
  id: number | string;
  itemType?: ItemType;
  hasPermission?: boolean;
  onHover: (dragIndex: number, hoverIndex: number) => void;
  onDrop?: (dragIndex: number, hoverIndex?: number) => void;
}

function Draggable({
  text,
  children,
  index,
  id,
  onHover,
  onDrop,
  itemType = ItemType.Card,
  hasPermission = true,
}: Props): JSX.Element {
  const content = children || text;
  const containerRef = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop({
    accept: itemType,
    hover(item: DragItem) {
      if (!containerRef?.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;

      onHover(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
    drop(item: DragItem) {
      if (!containerRef?.current) return;
      onDrop && onDrop(item.index, index);
    },
  });

  const [{ isDragging }, drag] = useDrag({
    item: { id, index, type: itemType },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const style = isDragging ? { opacity: 0, cursor: 'grabbing' } : { opacity: 1, cursor: 'grab' };
  drag(drop(containerRef));

  if (!hasPermission) {
    return <div>{content}</div>;
  }
  return (
    <div ref={containerRef} style={style}>
      {content}
    </div>
  );
}

export default Draggable;
